<template>
  <div class="columns">
    <div class="column is-four-fifths">
      <b-message type="is-info">
        This section defines the Evolution networks this remote is allowed to associate with. Making changes here will not update the NMS; resync will be required.
      </b-message>
      <table class="table is-fullwidth" v-if="kymetaBeams?.length && !hubStateLoading">
        <thead>
          <tr>
            <th style="width: 40px"></th>
            <th>Name</th>
            <th class="has-text-centered">Beam&nbsp;Id</th>
            <th class="has-text-centered">Desired</th>
            <th class="has-text-centered">Actual</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="beam in kymetaBeams" :key="beam.networkId">
            <td>
              <b-button title="View template" icon-right="file-alt" @click="openViewTemplateModal(beam.displayName)"></b-button>
            </td>
            <td>
              <p><strong>Internal:</strong> {{ beam.networkName }}</p>
              <p><strong>Display:</strong> {{ beam.displayName }}</p>
            </td>
            <td class="has-text-centered">{{ beam.networkId }}</td>
            <td class="has-text-centered">
              <template v-if="editing">
                <b-checkbox :disabled="beam.isRequired && beamsSelectedDuringEditing.includes(beam.networkId) && onlyOneBeamRemains" v-model="beamsSelectedDuringEditing" :native-value="beam.networkId"></b-checkbox>
              </template>
              <font-awesome-icon class="has-text-success" :icon="['far', 'check-circle']" v-if="beam.isRequired && !editing" />
            </td>
            <td class="has-text-centered">
              <font-awesome-icon class="has-text-success" :icon="['far', 'check-circle']" v-if="beam.isActuallyOn" />
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="hubStateLoading">
        Loading...
      </div>
      <div v-if="!hubStateLoading && !hasActiveSubscription">
        <p>Terminal must have an active subscription to retrieve network beam state.</p>
      </div>
      <div v-if="!hubStateLoading && !kymetaBeams?.length">
        <p class="mb-3">No network inventory found.</p>
        <b-message class="is-warning req-conditions-msg">
          <p class="content">
            Please note, to see available Beams, the Terminal must have have the following set:
            <ul>
              <li>Active Subscription</li>
              <li>Modem Model</li>
              <li>Modem Serial</li>
              <li>Antenna Model</li>
            </ul>
          </p>
        </b-message>
      </div>
      <template v-if="template">
        <hr/>
        <b-field label="Template">
              <b-input
                type="textarea"
                rows="20"
                v-model="template" 
                readonly/>
        </b-field>
      </template>
    </div>
    <div class="column is-one-fifth">
      <div class="px-4 action-links-container" v-if="$hasRole('Super User,Kymeta Admin,Product Support')">
        <p v-if="!editing">
          <a @click="toggleEditBeams(true)">Edit Beams</a>
        </p>
        <div v-else>
          <p><a @click="toggleEditBeams(false)">Cancel</a></p>
          <p class="mt-2">
            <a @click="saveNetworkRemoteMappings">Save</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, getCurrentInstance } from 'vue';
import { orderBy } from 'lodash';
import { useTerminalsStore } from '../terminals-store';
import { useAdminNetworkStore } from '../../admin/stores/admin-network-store';
import TerminalNetworkBeamTemplateModal from './TerminalNetworkBeamTemplateModal.vue';

const app = getCurrentInstance();
const terminalsStore = useTerminalsStore();
const adminNetworkStore = useAdminNetworkStore();
const terminal = computed(() => terminalsStore.state.terminal);
const hubState = computed(() => terminalsStore.state.hubState);
const hubStateLoading = computed(() => terminalsStore.state.hubStateLoading);

const hasActiveSubscription = computed(() => !!(terminal?.value?.activeSubscription));
// Kymeta Beams comes from hubState
const kymetaBeams = computed(() => (terminalsStore.state.hubState?.kymetaBeams ? orderBy(terminalsStore.state.hubState?.kymetaBeams, ['networkName'], ['asc']) : []));
const syncInProgress = computed(() => terminal?.value?.syncInProgress);

const editing = ref(false);
const isSaving = ref(false);
const template = ref('');

const beamsSelectedDuringEditing = ref([]);
const onlyOneBeamRemains = computed(() => beamsSelectedDuringEditing.value.length == 1);

const openViewTemplateModal = (displayName) => {
  // get nms type
  const nmsType = hubState?.value?.kymetaBeams?.length ? hubState?.value?.kymetaBeams[0].nmsType : null;
  if (!nmsType) return;

  // get antenna type
  const isU8 = terminal?.value?.antenna?.model?.toLowerCase()?.includes('u8');

  app.proxy.$buefy.modal.open({
    hasModalCard: true,
    component: TerminalNetworkBeamTemplateModal,
    trapFocus: true,
    width: 600,
    props: {
      nmsType,
      antennaType: isU8 ? 'u8' : 'u7',
      modemType: terminal?.value?.hardware?.geoModem?.model,
      beamName: displayName
    }
  });
}

const toggleEditBeams = (value) => {
  if (value) { // toggling to edit
    beamsSelectedDuringEditing.value = kymetaBeams.value.filter(n => n.isRequired).map(n => n.networkId);
  } else { // toggling to cancel
    beamsSelectedDuringEditing.value = [];
  }
  editing.value = value;
}

const saveNetworkRemoteMappings = async () => {
  isSaving.value = true;
  if (hubState?.value?.kymetaBeams?.length) {
    const nmsId = hubState?.value?.kymetaBeams[0].nmsId;
    const payload = {
      nmsId,
      remoteId: terminal?.value?.id,
      networkIds: beamsSelectedDuringEditing.value
    }
    const addResult = await adminNetworkStore.dispatch('addNetworkRemoteMapping', payload);
    if (!addResult) {
      app.proxy.$buefy.toast.open({ duration: 3000, type: 'is-danger', message: 'Unable to save networks.' });
      return;
    }
    app.proxy.$buefy.toast.open({ duration: 3000, type: 'is-success', message: 'Networks saved successfully.' });
    await terminalsStore.dispatch('fetchTerminalHubState');
  }  
  editing.value = false;
  isSaving.value = false;
}
</script>
